// ============================================
// Cloudinary image optimization helper
// Inserts f_auto,q_auto transformations so images are served as WebP/AVIF
// (when browser supports) with auto quality. Reduces image size by 10-15x
// without visible quality loss.
// ============================================

/**
 * Optimize a Cloudinary URL by injecting transformation parameters.
 * Non-Cloudinary URLs, invalid input, and URLs that already have transformations
 * are returned unchanged.
 *
 * @param {string} url — the original image URL
 * @param {object} opts — optional overrides (width, height, fit)
 * @returns {string} optimized URL or the original if not Cloudinary
 *
 * @example
 *   cldOpt('https://res.cloudinary.com/ddwsfn1xz/image/upload/v1776/abc.png')
 *   → 'https://res.cloudinary.com/ddwsfn1xz/image/upload/f_auto,q_auto/v1776/abc.png'
 *
 *   cldOpt(url, { width: 400 })
 *   → '.../upload/f_auto,q_auto,w_400/v1776/abc.png'
 */
export const cldOpt = (url, opts = {}) => {
  if (!url || typeof url !== 'string') return url;
  // Only transform Cloudinary URLs
  if (!url.includes('res.cloudinary.com')) return url;
  // Don't transform if we've already added transformations (detect by /upload/<trans>/v...)
  // Cloudinary transformations are comma-separated tokens between /upload/ and /v<digits>/
  // Skip if we see f_auto, q_auto, or w_ already in the URL
  if (/\/upload\/[^/]*(?:f_auto|q_auto|w_\d+)/.test(url)) return url;

  const parts = ['f_auto', 'q_auto'];
  if (opts.width)  parts.push(`w_${opts.width}`);
  if (opts.height) parts.push(`h_${opts.height}`);
  if (opts.fit)    parts.push(`c_${opts.fit}`); // c_fill, c_fit, c_scale

  const transforms = parts.join(',');
  // Inject after /upload/ → /upload/f_auto,q_auto/
  return url.replace(/\/upload\//, `/upload/${transforms}/`);
};

/**
 * Shortcut for product card thumbnails (small, fast)
 */
export const cldCard = (url) => cldOpt(url, { width: 600 });

/**
 * Shortcut for product gallery thumbnails (tiny)
 */
export const cldThumb = (url) => cldOpt(url, { width: 150 });

/**
 * Shortcut for full-size product hero images
 */
export const cldHero = (url) => cldOpt(url, { width: 1200 });

export default cldOpt;
